import { useState } from 'react';
import {
  billing as billingApi,
  notifications as notificationsApi,
  support as supportApi,
  team as teamApi,
  usage as usageApi,
} from '../api/resources.js';
import {
  Banner,
  Card,
  Empty,
  ErrorState,
  Loading,
  Meter,
  Stat,
  StatusPill,
} from '../components/ui.js';
import { useToast } from '../components/toast.js';
import { formatCurrency, formatDateTime, formatNumber, formatRelative } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/** Consumo (PRD §56) e analytics (PRD §125). */
export function UsagePage() {
  const [metric, setMetric] = useState('api_request');

  const usage = useAsync(() => usageApi.current(), []);
  const series = useAsync(() => usageApi.series(metric), [metric]);

  if (usage.loading) return <Loading />;
  if (usage.error) return <ErrorState error={usage.error} onRetry={usage.reload} />;

  const metrics = usage.data?.metrics ?? [];
  const peak = Math.max(1, ...(series.data?.data ?? []).map((point) => point.quantity));

  return (
    <>
      <div className="grid grid-4">
        {metrics
          .filter((entry) => entry.limit !== null)
          .slice(0, 4)
          .map((entry) => (
            <Stat
              key={entry.metric}
              label={entry.metric}
              value={`${formatNumber(entry.used)}`}
              hint={`de ${formatNumber(entry.limit ?? 0)} · ${entry.percentUsed}%`}
              {...(entry.percentUsed >= 100
                ? { tone: 'danger' as const }
                : entry.percentUsed >= 80
                  ? { tone: 'warn' as const }
                  : {})}
            />
          ))}
      </div>

      <Card
        title="Limites do plano"
        description={`${usage.data?.plan.name} · período ${usage.data?.period}`}
      >
        <div className="stack">
          {metrics.map((entry) => (
            <div key={entry.metric}>
              <div className="spread">
                <span>{entry.metric}</span>
                <span className="subtle">
                  {formatNumber(entry.used)}
                  {entry.limit !== null ? ` / ${formatNumber(entry.limit)}` : ' (sem limite)'}
                </span>
              </div>
              {entry.limit !== null && <Meter percent={entry.percentUsed} />}
            </div>
          ))}
        </div>
      </Card>

      <Card
        title="Série diária"
        actions={
          <select value={metric} onChange={(event) => setMetric(event.target.value)}>
            {metrics.map((entry) => (
              <option key={entry.metric} value={entry.metric}>
                {entry.metric}
              </option>
            ))}
          </select>
        }
      >
        {series.loading ? (
          <Loading />
        ) : (series.data?.data.length ?? 0) === 0 ? (
          <Empty title="Sem consumo no período" />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 3,
              height: 140,
              paddingTop: 8,
            }}
          >
            {(series.data?.data ?? []).map((point) => (
              <div
                key={point.date}
                title={`${point.date}: ${formatNumber(point.quantity)}`}
                style={{
                  flex: 1,
                  minWidth: 4,
                  // Proportional to the period's own peak, so a quiet month is still readable.
                  height: `${Math.max(2, (point.quantity / peak) * 100)}%`,
                  background: 'var(--brand)',
                  borderRadius: '3px 3px 0 0',
                }}
              />
            ))}
          </div>
        )}
        <div className="subtle" style={{ marginTop: 8 }}>
          <a href={`/v1/usage/export?metric=${metric}`}>Exportar CSV</a>
        </div>
      </Card>
    </>
  );
}

/** Plano e cobrança (PRD §53, §129). */
/** Equipe (PRD §20, §21). */
export function TeamPage() {
  const toast = useToast();
  const members = useAsync(() => teamApi.list(), []);

  const changeRole = useAction(async (membershipId: string, role: string) => {
    await teamApi.changeRole(membershipId, role);
    toast.success('Papel atualizado');
    members.reload();
  });

  return (
    <Card
      title="Membros"
      description="Recursos pertencem à organização, nunca a um usuário."
      bodyless
    >
      {members.loading ? (
        <Loading />
      ) : members.error ? (
        <ErrorState error={members.error} onRetry={members.reload} />
      ) : (
        <>
          {changeRole.error != null && (
            <div className="card-body">
              <ErrorState error={changeRole.error} />
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Papel</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(members.data?.data ?? []).map((member) => (
                  <tr key={member.id}>
                    <td>{member.name ?? '—'}</td>
                    <td className="subtle">{member.email ?? '—'}</td>
                    <td>
                      <select
                        aria-label={`Papel de ${member.name ?? member.userId}`}
                        value={member.role}
                        onChange={(event) => void changeRole.run(member.id, event.target.value)}
                        style={{ width: 'auto' }}
                      >
                        {['OWNER', 'ADMIN', 'DEVELOPER', 'BILLING', 'SUPPORT', 'VIEWER'].map(
                          (role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ),
                        )}
                      </select>
                    </td>
                    <td>
                      <StatusPill status={member.status} label={member.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

/** Alert Center (PRD §122). */
export function NotificationsPage() {
  const list = useAsync(() => notificationsApi.list(), []);
  const markRead = useAction(async (id: string) => {
    await notificationsApi.markRead(id);
    list.reload();
  });

  if (list.loading) return <Loading />;
  if (list.error) return <ErrorState error={list.error} onRetry={list.reload} />;

  const items = list.data?.data ?? [];

  return (
    <Card
      title="Notificações"
      description="Alertas de consumo, cobrança, webhooks e incidentes."
      bodyless
    >
      {items.length === 0 ? (
        <Empty title="Nada por aqui">
          <p className="muted">Você será avisado sobre consumo, falhas e cobrança.</p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Severidade</th>
                <th>Título</th>
                <th>Quando</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((notification) => (
                <tr key={notification.id}>
                  <td>
                    <span
                      className={`pill pill-${
                        notification.severity === 'CRITICAL'
                          ? 'danger'
                          : notification.severity === 'WARNING'
                            ? 'warn'
                            : notification.severity === 'RESOLVED'
                              ? 'ok'
                              : 'neutral'
                      }`}
                    >
                      {notification.severity}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: notification.readAt ? 400 : 600 }}>
                      {notification.title}
                    </div>
                    {notification.body && <div className="subtle">{notification.body}</div>}
                  </td>
                  <td className="subtle">{formatRelative(notification.createdAt)}</td>
                  <td>
                    {!notification.readAt && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => void markRead.run(notification.id)}
                      >
                        Marcar como lida
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Suporte (PRD §63, §64). */
export function SupportPage() {
  const toast = useToast();
  const [form, setForm] = useState({ category: 'instance', subject: '', description: '' });

  const tickets = useAsync(() => supportApi.tickets(), []);
  const open = useAction(async () => {
    await supportApi.open(form);
    toast.success('Chamado aberto', 'O diagnóstico do ambiente foi anexado automaticamente.');
    setForm({ category: 'instance', subject: '', description: '' });
    tickets.reload();
  });

  return (
    <>
      <Card
        title="Abrir chamado"
        description="Anexamos automaticamente plano, instâncias, erros recentes e saúde do provedor."
      >
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void open.run();
          }}
        >
          <div className="form-row">
            <div>
              <label htmlFor="ticket-category">Categoria</label>
              <select
                id="ticket-category"
                value={form.category}
                onChange={(event) => setForm((c) => ({ ...c, category: event.target.value }))}
              >
                <option value="instance">Instância</option>
                <option value="api">API</option>
                <option value="webhook">Webhook</option>
                <option value="billing">Cobrança</option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div style={{ flex: '3 1 260px' }}>
              <label htmlFor="ticket-subject">Assunto</label>
              <input
                id="ticket-subject"
                value={form.subject}
                onChange={(event) => setForm((c) => ({ ...c, subject: event.target.value }))}
                required
                minLength={5}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="ticket-description">Descrição</label>
            <textarea
              id="ticket-description"
              rows={4}
              value={form.description}
              onChange={(event) => setForm((c) => ({ ...c, description: event.target.value }))}
              required
              minLength={10}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={open.pending}>
            {open.pending ? 'Enviando…' : 'Abrir chamado'}
          </button>
        </form>
        {open.error != null && (
          <div style={{ marginTop: 12 }}>
            <ErrorState error={open.error} />
          </div>
        )}
      </Card>

      <Card title="Meus chamados" bodyless>
        {tickets.loading ? (
          <Loading />
        ) : (tickets.data?.data.length ?? 0) === 0 ? (
          <Empty title="Nenhum chamado aberto" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Assunto</th>
                  <th>Prioridade</th>
                  <th>Status</th>
                  <th>Aberto</th>
                </tr>
              </thead>
              <tbody>
                {(tickets.data?.data ?? []).map((ticket) => (
                  <tr key={ticket.id}>
                    <td>{ticket.subject}</td>
                    <td className="subtle">{ticket.priority}</td>
                    <td>
                      <StatusPill status={ticket.status} label={ticket.status} />
                    </td>
                    <td className="subtle">{formatRelative(ticket.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
